import { httpsCallable } from 'firebase/functions';

import {
  collection,
  addDoc,
  onSnapshot,
  doc,
  getDoc,
  getFirestore,
  runTransaction,
  Transaction,
} from 'firebase/firestore';

import { getFbFunctions } from './development';
import { getAuth, getIdTokenResult } from 'firebase/auth';
import { getApp } from 'firebase/app';
import type { CheckoutSession, CheckoutSessionError, EntitlementsResult, PlanName, StripePortalLink } from './types';
import { planLimits } from '../assets/js/helpers/BillingPlanHelper';

const INFINITE_LIMIT_SENTINEL = 99999;

export async function getStripePortal(returnUrl = `${window.location.origin}/app/settings`) {
  const fbApp = getApp(this.firebaseApp);
  // External functions are in us-central1
  const functions = getFbFunctions(fbApp, 'us-central1');

  try {
    const portalLink = httpsCallable(
      functions,
      'ext-firestore-stripe-payments-createPortalLink',
    );

    const result = (await portalLink({ returnUrl: returnUrl })).data as StripePortalLink;
    return result.url;
  } catch (e: any) {
    console.warn(e);
    const result = 'https://billing.stripe.com/p/login/dR603281y7rD4bCcMM';
    return result;
  }
}

export async function checkout(
  { price }:
  { price: string },
) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  const success = `https://${import.meta.env.VITE_HOSTNAME}/app/dashboard?subscribe=true`;
  const cancel = `https://${import.meta.env.VITE_HOSTNAME}/app/dashboard?cancel=true`;
  const return_url = `https://${import.meta.env.VITE_HOSTNAME}/app/dashboard`;

  try {
    const docRef = await addDoc(
      // Use Stripe MoR in development
      collection(db, 'users', this.user.uid, 'mor_checkout_sessions'),
      {
        price: price,
        automatic_tax: true, // Automatically calculate tax based on the customer's address
        allow_promotion_codes: true,
        success_url: success,
        cancel_url: cancel,
        return_url: return_url,
        consent_collection: {
          promotions: 'auto',
        },
        after_expiration: {
          recovery: {
            enabled: true,
            allow_promotion_codes: true,
          },
        },
        expires_at: Math.floor(Date.now() / 1000) + (3600 * 2), // 2 hours
      },
    );

    // Wait for Stripe Checkout URL or throw after 10 seconds
    await new Promise<void>((resolve, reject) => {
      // eslint-disable-next-line prefer-const
      let unsubscribe: (() => void) | undefined;
      const timeoutId = setTimeout(() => {
        unsubscribe?.();
        reject(new Error('Checkout session timed out'));
      }, 10_000);

      unsubscribe = onSnapshot(docRef, (snap) => {
        const session = snap.data() as CheckoutSession | CheckoutSessionError;
        if ('error' in session) {
          clearTimeout(timeoutId);
          unsubscribe?.();
          reject(new Error(session.error.message));
        } else if ('url' in session) {
          clearTimeout(timeoutId);
          unsubscribe?.();
          window.location.assign(session.url);
          resolve();
        }
      });
    });
  } catch (e: any) {
    console.error('MoR checkout failed', e);
    // While MoR is in beta, fall back to normal checkout on failure
    try {
      const docRef = await addDoc(
        collection(db, 'users', this.user.uid, 'checkout_sessions'),
        {
          price: price,
          automatic_tax: true, // Automatically calculate tax based on the customer's address
          allow_promotion_codes: true,
          success_url: success,
          cancel_url: cancel,
          return_url: return_url,
          consent_collection: {
            promotions: 'auto',
          },
          after_expiration: {
            recovery: {
              enabled: true,
              allow_promotion_codes: true,
            },
          },
          expires_at: Math.floor(Date.now() / 1000) + (3600 * 2), // 2 hours
        },
      );

      // Wait for Stripe Checkout URL or throw after 10 seconds
      await new Promise<void>((resolve, reject) => {
        // eslint-disable-next-line prefer-const
        let unsubscribe: (() => void) | undefined;
        const timeoutId = setTimeout(() => {
          unsubscribe?.();
          reject(new Error('Checkout session timed out'));
        }, 10_000);

        unsubscribe = onSnapshot(docRef, (snap) => {
          const session = snap.data() as CheckoutSession | CheckoutSessionError;
          if ('error' in session) {
            clearTimeout(timeoutId);
            unsubscribe?.();
            reject(new Error(session.error.message));
          } else if ('url' in session) {
            clearTimeout(timeoutId);
            unsubscribe?.();
            window.location.assign(session.url);
            resolve();
          }
        });
      });
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
}

export async function currentSubscription() {
  try {
    const fbApp = getApp(this.firebaseApp);
    const db = getFirestore(fbApp, this.dbid);
    const auth = getAuth(fbApp);

    if (!auth.currentUser) {
      return 'Free';
    }

    if (this.team != null && this.userMode == 'team') {
      const docRef = doc(db, 'teams', this.team);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists() && docSnap.data().members.includes(auth.currentUser.uid)) {
        return 'Team';
      }
    }

    const decodedToken = await getIdTokenResult(auth.currentUser, true);
    switch (decodedToken?.claims?.stripeRole) {
      case 'basic':
        return 'Basic';
      case 'plus':
        return 'Plus';
      default:
        return 'Free';
    }
  } catch {
    return 'Free';
  }
}

export async function getEffectiveEntitlements(): Promise<EntitlementsResult> {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);
  const functions = getFbFunctions(fbApp);

  const rackCount = await this.getRackCount();
  const roomCount = await this.getRoomCount();
  const slotTypeCount = await this.getPrivateSlotCount();

  let plan = (await this.currentSubscription()).toLowerCase();
  if (plan == 'team') plan = 'free';
  const limits = planLimits[plan as keyof typeof planLimits];

  const entitlements = {
    plan: plan as PlanName,
    limits: {
      racks: limits.racks('value'),
      rooms: limits.rooms('value'),
      slotTypes: limits.slots('value'),
      users: limits.members('value'),
    },
    usage: {
      racks: rackCount,
      rooms: roomCount,
      slotTypes: slotTypeCount,
      users: 1,
    },
  };

  if (this.userMode == 'user' || this.team == null) {
    return entitlements;
  } else {
    const teamDocRef = doc(db, 'teams', this.team);
    const teamDocSnap = await getDoc(teamDocRef);

    if (!teamDocSnap.exists()) {
      entitlements.limits = {
        racks: 0,
        rooms: 0,
        slotTypes: 0,
        users: 0,
      };
      return entitlements;
    }

    entitlements.usage.users = teamDocSnap.data().members.length;

    // Attempt to get owner current sub doc for plan & limits
    const owner = teamDocSnap.data().owner;

    if (owner == this.user.uid) {
      const ownerDocRef = doc(db, 'users', owner, 'subscriptions', 'current');
      const ownerDocSnap = await getDoc(ownerDocRef);
      if (ownerDocSnap.exists()) {
        const ownerSub = ownerDocSnap.data();
        entitlements.plan = ownerSub.plan as PlanName;

        const teamLimits = planLimits[ownerSub.plan as keyof typeof planLimits];

        entitlements.limits = {
          racks: teamLimits.racks('value'),
          rooms: teamLimits.rooms('value'),
          slotTypes: teamLimits.slots('value'),
          users: teamLimits.members('value'),
        };

        if (ownerSub.status !== 'active' && ownerSub.status !== 'trialing' && ownerSub.status !== 'past_due') {
          entitlements.limits = {
            racks: 0,
            rooms: 0,
            slotTypes: 0,
            users: 0,
          };

          entitlements.plan = 'Free';
        }
      }

      return entitlements;
    } else {
      const getEntitlements = httpsCallable(functions, 'entitlements-getEntitlements');
      const result = await getEntitlements({ teamId: this.team });
      const entitlementsResponse = result.data as EntitlementsResult;

      plan = entitlementsResponse.plan.toLowerCase();
      entitlements.plan = entitlementsResponse.plan;
      const teamLimits = planLimits[plan as keyof typeof planLimits];

      entitlements.limits = {
        racks: teamLimits.racks('value'),
        rooms: teamLimits.rooms('value'),
        slotTypes: teamLimits.slots('value'),
        users: teamLimits.members('value'),
      };

      if (entitlements.limits.racks == INFINITE_LIMIT_SENTINEL) entitlements.limits.racks = Infinity;
      if (entitlements.limits.rooms == INFINITE_LIMIT_SENTINEL) entitlements.limits.rooms = Infinity;

      if (entitlements.plan == 'Free') {
        entitlements.limits = {
          racks: 0,
          rooms: 0,
          slotTypes: 0,
          users: 0,
        };
      }

      return entitlements;
    }
  }
}

export async function runSubscriptionTransaction(
  action: (subscription: 'Free' | 'Basic' | 'Plus', transaction: Transaction) => Promise<string>,
) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);
  const auth = getAuth(fbApp);

  return await runTransaction(db, async (tx) => {
    let sub = 'Free';

    if (auth.currentUser) {
      if (this.team != null && this.userMode == 'team') {
        const docSnap = await tx.get(doc(db, 'teams', this.team));

        if (docSnap.exists() && docSnap.data().members.includes(auth.currentUser.uid)) {
          sub = 'Team';
        }
      }
      else {
        // Refresh token to get latest subscription
        const decodedToken = await getIdTokenResult(auth.currentUser, true);
        switch (decodedToken?.claims?.stripeRole) {
          case 'basic':
            sub = 'Basic';
            break;
          case 'plus':
            sub = 'Plus';
            break;
          default:
            sub = 'Free';
            break;
        }
      }
    }

    return await action(sub as 'Free' | 'Basic' | 'Plus', tx);
  });
}

export function buildRedirectURL() {
  const isPlus = this.purchaseFlow.includes('plus');
  const isYearly = this.purchaseFlow.includes('yearly');
  const plan = isPlus ? 'plus' : 'basic';

  let redirectURL = `https://${import.meta.env.VITE_HOSTNAME}/auth/verifyEmail?purchase=${plan}`;
  if (isYearly) {
    redirectURL += '&yearly=true';
  }

  return redirectURL;
}

